import { Editor } from '@tiptap/core'
import { EditorState, PluginKey } from '@tiptap/pm/state'
import { npubEncode } from '@nostr/tools/nip19'
import {
  findInstalledCommands,
  installedToDefinition,
  TInstalledCommand
} from '@/atoms/slash-commands'
import { commandAppliesToEvent, getCommandRelayUrls } from '@/lib/slash-command-definition'
import { getEditorContext } from './composerContext'
import { setActiveArgs } from './suggestionState'
import { createSuggestionRender } from './slash-popup'
import SlashCommandList from './SlashCommandList'

type RendererProps = {
  editor: Editor
  items: { command: TInstalledCommand }[]
  command: (payload: { command: TInstalledCommand }) => void
}

const slashSuggestion = {
  char: '/',
  pluginKey: new PluginKey('slashCommandSuggestion'),
  startOfLine: true,

  // only trigger when "/" is the first thing in the textarea
  allow: ({ state, range }: { state: EditorState; range: { from: number } }) => {
    return state.doc.textBetween(0, range.from, '\n', ' ').trim() === ''
  },

  items: ({ editor, query }: { editor: Editor; query: string }) => {
    const context = getEditorContext(editor)
    // without composer context we can't know if the commands apply, so show nothing
    if (!context) return []

    const q = query.toLowerCase()
    const installed = findInstalledCommands(query, 20)

    return installed
      .filter((command) => commandAppliesToEvent(command, context.targetKind, context.targetTags))
      .filter(
        (command) =>
          !q ||
          command.command.toLowerCase().includes(q) ||
          (command.title ?? '').toLowerCase().includes(q)
      )
      .map((command) => ({ command }))
  },

  command: ({
    editor,
    range,
    props
  }: {
    editor: Editor
    range: { from: number; to: number }
    props: { command: TInstalledCommand }
  }) => {
    const { command } = props
    const context = getEditorContext(editor)
    const npub = npubEncode(command.pubkey)

    // relay: scoped relays are added to the RelaySelector on insert
    if (context) {
      const relayUrls = getCommandRelayUrls(installedToDefinition(command))
      if (relayUrls.length > 0) context.addRelays(relayUrls)
    }

    // remember which installed command the args suggestion applies to
    setActiveArgs({ pubkey: command.pubkey, d: command.d })

    editor
      .chain()
      .focus()
      .insertContentAt(range, [
        {
          type: 'slashCommand',
          attrs: { command: command.command, pubkey: command.pubkey, d: command.d, npub }
        },
        {
          type: 'text',
          text: ' '
        }
      ])
      .run()
  },

  render: createSuggestionRender<RendererProps>(SlashCommandList)
}

export default slashSuggestion
